import {skipToken} from '@reduxjs/toolkit/query';
import {isFeatureEnabled} from 'feature-flags';
import {Pattern, match} from 'ts-pattern';

import {isNil, isNotNil} from 'ramda';

import {useAiAssistantPresentation} from '@dms/ai-assistant';
import {useGetTenantV2Query} from '@dms/api/base';
import {
  useListCustomerMessagesQuery,
  useListSignaturesQuery,
  useReadCodeQuery,
} from '@dms/api/core';
import {useGetCustomerV2Query} from '@dms/api/customer';
import {
  type InterestResponseBodyV2Read,
  useGetInterestV2Query,
  useGetMinisaleQuery,
  useGetMinipurchaseQuery,
  useGetOfferedVehiclesListQuery,
} from '@dms/api/finance';
import {
  useGetAiLeadEmailMessageQuery,
  useGetInterestActivityListQuery,
  useGetInterestUpcomingTasksQuery,
} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {featureFlags} from '@dms/feature-flags';
import {useUserFullName, useVehicleMake, useVehicleModel} from '@dms/shared';

import {
  DEFAULT_INTEREST_SIGNAL_POLICY,
  type InterestSignalPolicy,
} from '@prompt-bridge/shared/ai-assistant-core';

import type {ToSalesInterestFactsInput} from '../types/ToSalesInterestFactsInput';
import {toAssistantInterestSender} from '../utils/toAssistantInterestSender';
import {toSalesInterestAssistantContext} from '../utils/toSalesInterestAssistantContext';
import {usePublishAssistantInterestContext} from './usePublishAssistantInterestContext';

type SalesInterestForAssistant = ToSalesInterestFactsInput['interest'];

type UseAssistantSalesInterestContextParams = {
  interestId: string;
  signalPolicy?: InterestSignalPolicy;
};

const isSalesInterestForAssistant = (
  interest: InterestResponseBodyV2Read | undefined
): interest is SalesInterestForAssistant =>
  isNotNil(interest?.createdAt) &&
  match(interest.state)
    .with('NEW', 'OPEN', 'CLOSED', 'UNSUCCESSFUL', () => true)
    .otherwise(() => false);

const hasOfferedVehicleIds = (
  offeredVehicleIds: string[] | null | undefined
): offeredVehicleIds is string[] => isNotNil(offeredVehicleIds) && offeredVehicleIds.length > 0;

const isOptionalOriginResourceReady = (
  resourceId: string | undefined,
  hasData: boolean,
  isError: boolean
) =>
  match({resourceId, hasData, isError})
    .with({resourceId: Pattern.nullish}, () => true)
    .with({hasData: true}, () => true)
    .with({isError: true}, () => true)
    .otherwise(() => false);

// eslint-disable-next-line complexity -- Pre-existing violation
export function useAssistantSalesInterestContext(params: UseAssistantSalesInterestContextParams) {
  const {isOpen: isAssistantOpen} = useAiAssistantPresentation();
  const isAssistantEnabled = isFeatureEnabled(featureFlags.OMNETIC_AI_ASSISTANT);
  const shouldFetchInterest = isAssistantEnabled && isAssistantOpen;
  const {
    currentData: interest,
    isError: isInterestError,
    refetch: refetchInterest,
  } = useGetInterestV2Query({id: params.interestId}, {skip: !shouldFetchInterest});
  const shouldFetchDetails =
    shouldFetchInterest && isSalesInterestForAssistant(interest) && isNotNil(interest.customerId);
  const senderDisplayName = useUserFullName();
  const {
    currentData: activities,
    isError: isActivitiesError,
    refetch: refetchActivities,
  } = useGetInterestActivityListQuery({interestId: params.interestId}, {skip: !shouldFetchDetails});
  const {currentData: upcomingTasks, isError: isUpcomingTasksError} =
    useGetInterestUpcomingTasksQuery({interestId: params.interestId}, {skip: !shouldFetchDetails});
  // Inbound customer emails never become sales activities, so the signal engine would otherwise
  // never see that this customer is waiting for a reply.
  const {
    currentData: customerMessages,
    isError: isCustomerMessagesError,
    refetch: refetchCustomerMessages,
  } = useListCustomerMessagesQuery(
    shouldFetchDetails && isNotNil(interest?.customerId)
      ? {
          customerId: interest.customerId,
          recordId: params.interestId,
          resourceId: EntityResourceIds.interest,
        }
      : skipToken
  );
  const {currentData: customer, isError: isCustomerError} = useGetCustomerV2Query(
    shouldFetchDetails && isNotNil(interest?.customerId)
      ? {customerId: interest.customerId}
      : skipToken
  );
  const {data: tenant, isError: isTenantError} = useGetTenantV2Query(undefined, {
    skip: !shouldFetchDetails,
  });
  const {
    currentData: signatures,
    isError: isSignaturesError,
    isFetching: isSignaturesFetching,
    isLoading: isSignaturesQueryLoading,
  } = useListSignaturesQuery(undefined, {
    skip: !shouldFetchDetails,
  });
  const sourceCodeId = shouldFetchDetails ? (interest?.sourceCodeId ?? undefined) : undefined;
  const sourceCodeQuery = useReadCodeQuery(
    isNotNil(sourceCodeId) ? {codelistId: 'interest_source', codeId: sourceCodeId} : skipToken
  );
  const originLeadMessageId = shouldFetchDetails
    ? (interest?.originLeadMessageId ?? undefined)
    : undefined;
  const originLeadMessageQuery = useGetAiLeadEmailMessageQuery(
    isNotNil(originLeadMessageId) ? {id: originLeadMessageId} : skipToken
  );
  const minipurchaseId = shouldFetchDetails ? interest?.minipurchasesIds?.at(-1) : undefined;
  const {currentData: minipurchase} = useGetMinipurchaseQuery(
    isNotNil(minipurchaseId) ? {minipurchaseId} : skipToken
  );
  const minisaleId = shouldFetchDetails ? interest?.minisalesIds?.at(-1) : undefined;
  const {currentData: minisale} = useGetMinisaleQuery(
    isNotNil(minisaleId) ? {minisaleId} : skipToken
  );
  const [{makeOptions: minipurchaseMakeOptions}, {isLoading: isMinipurchaseMakeOptionsLoading}] =
    useVehicleMake(shouldFetchDetails ? minipurchase?.filteredSpecifications?.type : null);
  const [{makeOptions: minisaleMakeOptions}, {isLoading: isMinisaleMakeOptionsLoading}] =
    useVehicleMake(shouldFetchDetails ? minisale?.vehicleData?.type : null);
  const [{modelOptions: minipurchaseModelOptions}, {isLoading: isMinipurchaseModelOptionsLoading}] =
    useVehicleModel(
      shouldFetchDetails ? minipurchase?.filteredSpecifications?.type : null,
      minipurchase?.filteredSpecifications?.make
    );
  const [{modelOptions: minisaleModelOptions}, {isLoading: isMinisaleModelOptionsLoading}] =
    useVehicleModel(
      shouldFetchDetails ? minisale?.vehicleData?.type : null,
      isNotNil(minisale?.vehicleData?.make) ? [minisale.vehicleData.make] : null
    );
  const offeredVehicleIds = minipurchase?.offeredVehiclesIds;
  const {currentData: offeredVehicles} = useGetOfferedVehiclesListQuery(
    hasOfferedVehicleIds(offeredVehicleIds) ? {body: {vehicleIds: offeredVehicleIds}} : skipToken
  );
  const isMinipurchaseReady = isNil(minipurchaseId) || isNotNil(minipurchase);
  const isMinisaleReady = isNil(minisaleId) || isNotNil(minisale);
  const areOfferedVehiclesReady =
    !hasOfferedVehicleIds(offeredVehicleIds) || isNotNil(offeredVehicles);
  const areVehicleOptionsReady =
    !isMinipurchaseMakeOptionsLoading &&
    !isMinisaleMakeOptionsLoading &&
    !isMinipurchaseModelOptionsLoading &&
    !isMinisaleModelOptionsLoading;
  const areCustomerMessagesReady = isNotNil(customerMessages) || isCustomerMessagesError;
  const isSignaturesLoading =
    shouldFetchDetails &&
    isNil(signatures) &&
    !isSignaturesError &&
    (isSignaturesQueryLoading || isSignaturesFetching);
  const isSourceCodeReady = isOptionalOriginResourceReady(
    sourceCodeId,
    isNotNil(sourceCodeQuery.currentData),
    sourceCodeQuery.isError
  );
  const isOriginLeadMessageReady = isOptionalOriginResourceReady(
    originLeadMessageId,
    isNotNil(originLeadMessageQuery.currentData),
    originLeadMessageQuery.isError
  );
  const hasRequiredQueryError =
    shouldFetchInterest &&
    (isInterestError ||
      (shouldFetchDetails &&
        (isActivitiesError || isUpcomingTasksError || isCustomerError || isTenantError)));
  const isContextLoading =
    shouldFetchInterest &&
    !hasRequiredQueryError &&
    (isNil(interest) ||
      (shouldFetchDetails &&
        (isNil(activities) ||
          isNil(upcomingTasks) ||
          isNil(customer) ||
          isNil(tenant) ||
          !areCustomerMessagesReady ||
          isSignaturesLoading ||
          !isMinipurchaseReady ||
          !isMinisaleReady ||
          !areOfferedVehiclesReady ||
          !areVehicleOptionsReady ||
          !isSourceCodeReady ||
          !isOriginLeadMessageReady)));
  const refreshAssistantInterestContext = async () => {
    if (!shouldFetchInterest) {
      return;
    }

    await Promise.all([
      refetchInterest(),
      ...(shouldFetchDetails ? [refetchActivities(), refetchCustomerMessages()] : []),
    ]);
  };
  const createAssistantInterestContext = (evaluatedAt: string) =>
    match({
      shouldFetchDetails,
      interest,
      activities,
      upcomingTasks,
      customer,
      tenant,
      areCustomerMessagesReady,
      isMinipurchaseReady,
      isMinisaleReady,
      areOfferedVehiclesReady,
      areVehicleOptionsReady,
      isSignaturesLoading,
      isSourceCodeReady,
      isOriginLeadMessageReady,
      hasRequiredQueryError,
    })
      .with(
        {
          shouldFetchDetails: true,
          interest: Pattern.when(isSalesInterestForAssistant),
          activities: Pattern.nonNullable,
          upcomingTasks: Pattern.nonNullable,
          customer: Pattern.nonNullable,
          tenant: Pattern.nonNullable,
          areCustomerMessagesReady: true,
          isMinipurchaseReady: true,
          isMinisaleReady: true,
          areOfferedVehiclesReady: true,
          areVehicleOptionsReady: true,
          isSignaturesLoading: false,
          isSourceCodeReady: true,
          isOriginLeadMessageReady: true,
          hasRequiredQueryError: false,
        },
        ({interest, activities, upcomingTasks, customer, tenant}) =>
          toSalesInterestAssistantContext({
            interestId: params.interestId,
            interest,
            activities,
            customerMessages: customerMessages?.messages ?? null,
            upcomingTasks,
            minipurchase: minipurchase ?? null,
            minisale: minisale ?? null,
            minipurchaseMakeOptions: minipurchaseMakeOptions ?? [],
            minipurchaseModelOptions: minipurchaseModelOptions ?? [],
            minisaleMakeOptions: minisaleMakeOptions ?? [],
            minisaleModelOptions: minisaleModelOptions ?? [],
            customer,
            tenant,
            offeredVehicles: offeredVehicles ?? [],
            sourceCode: sourceCodeQuery.currentData ?? null,
            originLeadMessage: originLeadMessageQuery.currentData ?? null,
            sender: toAssistantInterestSender(senderDisplayName, {
              signatureCount: signatures?.length,
              isLoading: isSignaturesLoading,
              isError: isSignaturesError,
            }),
            policy: params.signalPolicy ?? DEFAULT_INTEREST_SIGNAL_POLICY,
            evaluatedAt,
          })
      )
      .otherwise(() => undefined);

  return usePublishAssistantInterestContext(
    createAssistantInterestContext,
    [
      params.interestId,
      params.signalPolicy,
      shouldFetchDetails,
      interest,
      activities,
      customerMessages,
      upcomingTasks,
      customer,
      tenant,
      signatures,
      senderDisplayName ?? undefined,
      minipurchase,
      minisale,
      minipurchaseMakeOptions ?? undefined,
      minipurchaseModelOptions ?? undefined,
      minisaleMakeOptions ?? undefined,
      minisaleModelOptions ?? undefined,
      offeredVehicles,
      sourceCodeQuery.currentData,
      originLeadMessageQuery.currentData,
      isMinipurchaseReady,
      isMinisaleReady,
      areOfferedVehiclesReady,
      areVehicleOptionsReady,
      isSignaturesLoading,
      isSourceCodeReady,
      isOriginLeadMessageReady,
      areCustomerMessagesReady,
      hasRequiredQueryError,
    ],
    isContextLoading,
    refreshAssistantInterestContext
  );
}
